import { NetworkError } from '@/api/api';
import { ConnectionError } from '@/api/stateApi';
import ErrorTitle from '@/components/errorTitle';
import { notifications, type NotificationData } from '@mantine/notifications';
import { IconExclamationCircle } from '@tabler/icons-react';
import type { User } from 'backend/src/lib/auth';
import { calcDuration } from 'cross-state';
import { HTTPError } from 'ky';
import { useEffect, useRef, type ReactNode } from 'react';
import z, { ZodError } from 'zod';

export interface HandleErrorOptions extends Partial<NotificationData> {
  title?: string;
  user?: User;
}

let seenErrors = new Array<unknown>();

export function errorToString(error: unknown, { includeStack }: { includeStack?: boolean } = {}) {
  if (error instanceof Error) {
    if (includeStack) {
      return error.stack ?? error.message;
    }

    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return JSON.stringify(error, null, 2);
}

export function handleError(_error: unknown, options?: HandleErrorOptions): string | undefined {
  const error = _error instanceof Error ? _error : new Error(errorToString(_error));

  if (seenErrors.includes(error)) {
    return;
  }
  seenErrors = [...seenErrors, error].slice(-10);

  console.error(...[options?.title].filter(Boolean), error);

  const { title, message } = matchError(error);
  const errorDetails = getErrorDetails(error, options?.user);

  return (options?.id ? notifications.update : notifications.show)({
    color: 'red',
    icon: <IconExclamationCircle />,
    title: <ErrorTitle error={errorDetails}>{options?.title ?? title}</ErrorTitle>,
    message: message && (
      <div
        css={{
          maxHeight: '5em',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
        }}
      >
        {message}
      </div>
    ),
    withBorder: true,
    autoClose: calcDuration({ seconds: 10 }),
    ...options,
  });
}

export function getErrorDetails(error: unknown, user?: User) {
  return [
    `Time: ${new Date().toISOString()}`,
    `URL: ${window.location.href}`,
    `User agent: ${navigator.userAgent}`,
    `Version: ${import.meta.env.VERSION || '0.0.0'}`,
    user && `User: ${user.email}`,
    '---',
    errorToString(error, { includeStack: true }),
  ]
    .filter(Boolean)
    .join('\n');
}

export function matchError(error: unknown): { title: ReactNode; message: ReactNode } {
  if (error instanceof ZodError) {
    return {
      title: 'Validation error',
      message: z.prettifyError(error),
    };
  }

  if (error instanceof HTTPError) {
    switch (error.response.status) {
      case 401:
        return {
          title: 'Unauthorized',
          message: 'You are not authorized to perform this action. Please log in.',
        };
      case 403:
        return {
          title: 'Forbidden',
          message: 'You do not have permission to perform this action.',
        };
      case 404:
        return {
          title: 'Not Found',
          message: `The requested resource was not found: ${error.request.url}`,
        };
      case 409:
        return {
          title: 'Conflict',
          message: 'Someone else changed the same data in the meantime. Reload and try again.',
        };
      case 500:
        return {
          title: 'Server Error',
          message: 'An internal server error occurred. Please try again later.',
        };
      default:
        return {
          title: `HTTP Error ${error.response.status}`,
          message: `An HTTP error occurred with status code ${error.response.status}.`,
        };
    }
  }

  if (error instanceof NetworkError) {
    return {
      title: 'Network Error',
      message: `Could not fetch ${error.url}. Please check your network connection.`,
    };
  }

  if (error instanceof ConnectionError) {
    return {
      title: 'Websocket Connection Error',
      message: error.message,
    };
  }

  return {
    title: 'An error occurred',
    message: errorToString(error),
  };
}

export function useHandleError(errors: unknown | unknown[], options?: HandleErrorOptions) {
  const error = (Array.isArray(errors) ? errors : [errors]).find(Boolean);
  const user = undefined;
  const notificationId = useRef<string>(undefined);

  useEffect(() => {
    if (!error) {
      if (notificationId.current) {
        notifications.hide(notificationId.current);
        notificationId.current = undefined;
      }
      return;
    }

    notificationId.current = handleError(error, {
      user,
      id: notificationId.current,
      ...options,
    });

    // oxlint-disable-next-line exhaustive-deps
  }, [error]);

  useEffect(
    () => () => {
      if (notificationId.current) {
        notifications.hide(notificationId.current);
      }
    },
    [],
  );
}
